import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import GameQuestionForm from "../../../components/admin/game/GameQuestionForm";
import {
  getAdminGameQuestionById,
  presignAdminGameQuestionImage,
  updateAdminGameQuestion,
} from "../../../features/game/gameApi";
import cls from "./SingleGameQuestionPage.module.scss";

const slots = [
  { key: "background", label: "Game Background", field: "gameBackgroundImage" },
  { key: "display-1", label: "Display Image 1", field: "displayImage1" },
  { key: "display-2", label: "Display Image 2", field: "displayImage2" },
];

export default function SingleGameQuestionPage() {
  const { questionId } = useParams();

  const [question, setQuestion] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState({});
  const [uploadingKey, setUploadingKey] = useState("");

  const load = useCallback(
    async (showInitialLoader = false) => {
      if (showInitialLoader) setPageLoading(true);
      try {
        const data = await getAdminGameQuestionById(questionId);
        setQuestion(data.question || null);
      } finally {
        if (showInitialLoader) setPageLoading(false);
      }
    },
    [questionId],
  );

  useEffect(() => {
    load(true);
  }, [load]);

  const handleSubmit = async (payload) => {
    setSaving(true);
    try {
      await updateAdminGameQuestion(questionId, payload);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const uploadSlot = async (imageType) => {
    const file = files[imageType];
    if (!file) return;

    setUploadingKey(imageType);
    try {
      const ext = file.name.split(".").pop() || "jpg";
      const presign = await presignAdminGameQuestionImage(
        questionId,
        imageType,
        {
          ext,
          contentType: file.type || "image/jpeg",
        },
      );

      await fetch(presign.uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type || "image/jpeg",
        },
        body: file,
      });

      setFiles((p) => ({ ...p, [imageType]: null }));
      await load();
    } finally {
      setUploadingKey("");
    }
  };

  const stats = useMemo(() => {
    if (!question) {
      return {
        roundNumber: "-",
        answeringGender: "-",
        status: "-",
        uploadedImages: 0,
      };
    }

    const uploadedImages = slots.filter(
      (slot) => question?.[slot.field]?.url,
    ).length;

    return {
      roundNumber: question.roundNumber ?? "-",
      answeringGender: question.answeringGender ?? "-",
      status: question.isActive ? "Active" : "Inactive",
      uploadedImages,
    };
  }, [question]);

  if (pageLoading) {
    return (
      <div className={cls.page}>
        <div className={cls.bgGlowBlue} />
        <div className={cls.bgGlowPink} />
        <div className={cls.gridLines} />

        <div className={cls.container}>
          <div className={cls.stateCard}>
            <div className={cls.loader} />
            <h3>Loading question details</h3>
            <p>Please wait while we fetch the latest question data.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!question) {
    return (
      <div className={cls.page}>
        <div className={cls.bgGlowBlue} />
        <div className={cls.bgGlowPink} />
        <div className={cls.gridLines} />

        <div className={cls.container}>
          <div className={cls.stateCard}>
            <div className={cls.emptyIcon}>?</div>
            <h3>Question not found</h3>
            <p>The question could not be loaded or may no longer exist.</p>
            <Link className={cls.secondaryBtn} to="/admin/game-questions">
              Back to Questions
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.bgGlowBlue} />
      <div className={cls.bgGlowPink} />
      <div className={cls.gridLines} />

      <div className={cls.container}>
        <header className={cls.hero}>
          <div className={cls.heroCopy}>
            <span className={cls.kicker}>ADMIN · QUESTION DETAILS</span>
            <h1 className={cls.title}>Manage Question</h1>
            <p className={cls.subtitle}>
              Update the question details, review current media, and upload
              fresh visuals for the game experience.
            </p>
          </div>

          <div className={cls.heroActions}>
            <Link className={cls.secondaryBtn} to="/admin/game-questions">
              Back to Questions
            </Link>
          </div>
        </header>

        <section className={cls.statGrid}>
          <article className={cls.statCard}>
            <div className={cls.statTop}>
              <span className={cls.statDot} />
              <span className={cls.statLabel}>Round</span>
            </div>
            <div className={cls.statValue}>{stats.roundNumber}</div>
            <div className={cls.statMeta}>Assigned round number</div>
          </article>

          <article className={cls.statCard}>
            <div className={cls.statTop}>
              <span className={`${cls.statDot} ${cls.pinkDot}`} />
              <span className={cls.statLabel}>Answering</span>
            </div>
            <div className={cls.statValueCaps}>{stats.answeringGender}</div>
            <div className={cls.statMeta}>Target answer group</div>
          </article>

          <article className={cls.statCard}>
            <div className={cls.statTop}>
              <span
                className={`${cls.statDot} ${
                  question.isActive ? cls.activeDot : cls.warningDot
                }`}
              />
              <span className={cls.statLabel}>Status</span>
            </div>
            <div className={cls.statValueCaps}>{stats.status}</div>
            <div className={cls.statMeta}>Current visibility state</div>
          </article>

          <article className={cls.statCard}>
            <div className={cls.statTop}>
              <span className={`${cls.statDot} ${cls.blueDot}`} />
              <span className={cls.statLabel}>Media</span>
            </div>
            <div className={cls.statValue}>{stats.uploadedImages}/3</div>
            <div className={cls.statMeta}>Images currently uploaded</div>
          </article>
        </section>

        <section className={cls.panel}>
          <div className={cls.panelHead}>
            <div>
              <h2 className={cls.panelTitle}>Question Information</h2>
              <p className={cls.panelSub}>
                Edit the question content and save your latest changes.
              </p>
            </div>

            <div
              className={`${cls.statusBadge} ${
                question.isActive ? cls.active : cls.inactive
              }`}
            >
              {question.isActive ? "Active" : "Inactive"}
            </div>
          </div>

          <div className={cls.formWrap}>
            <GameQuestionForm
              initialValue={question}
              submitLabel={saving ? "Updating..." : "Update Question"}
              loading={saving}
              onSubmit={handleSubmit}
            />
          </div>
        </section>

        <section className={cls.panel}>
          <div className={cls.panelHead}>
            <div>
              <h2 className={cls.panelTitle}>Question Images</h2>
              <p className={cls.panelSub}>
                Upload background and supporting display images for this
                question.
              </p>
            </div>
          </div>

          <div className={cls.imagesGrid}>
            {slots.map((slot) => {
              const imageUrl = question?.[slot.field]?.url;
              const selectedFile = files[slot.key];
              const isUploading = uploadingKey === slot.key;

              return (
                <article className={cls.imageCard} key={slot.key}>
                  <div className={cls.imageHead}>
                    <div>
                      <h3 className={cls.imageTitle}>{slot.label}</h3>
                      <p className={cls.imageSub}>
                        {imageUrl
                          ? "Current image available"
                          : "No image uploaded yet"}
                      </p>
                    </div>

                    <div className={cls.slotBadge}>
                      {imageUrl ? "Uploaded" : "Empty"}
                    </div>
                  </div>

                  <div className={cls.previewWrap}>
                    {imageUrl ? (
                      <img
                        className={cls.preview}
                        src={imageUrl}
                        alt={slot.label}
                      />
                    ) : (
                      <div className={cls.emptyPreview}>
                        <span className={cls.emptyPreviewIcon}>+</span>
                        <p>No image selected</p>
                      </div>
                    )}
                  </div>

                  <div className={cls.uploadPanel}>
                    <label className={cls.uploadField}>
                      <span className={cls.uploadLabel}>Choose Image</span>
                      <input
                        className={cls.fileInput}
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          setFiles((p) => ({
                            ...p,
                            [slot.key]: e.target.files?.[0] || null,
                          }))
                        }
                      />
                    </label>

                    <div className={cls.selectedFile}>
                      {selectedFile
                        ? selectedFile.name
                        : "No new file selected"}
                    </div>

                    <button
                      className={cls.primaryBtn}
                      type="button"
                      onClick={() => uploadSlot(slot.key)}
                      disabled={!selectedFile || isUploading}
                    >
                      <span className={cls.btnGlow} />
                      <span>
                        {isUploading ? "Uploading..." : "Upload Image"}
                      </span>
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
